import { RefObject, useLayoutEffect, useState } from 'react';

/**
 * Position for a pill/underline that slides to the active child of a chip or tab row.
 * Put `position: relative` on the container, render an absolutely-positioned element with
 * the returned style (transition on transform + width), and mark each child with
 * `data-pill-index={i}`. Re-measures when `activeIndex` or `deps` change and on resize.
 */
export function useSlidingIndicator(
  containerRef: RefObject<HTMLElement>,
  activeIndex: number,
  deps: unknown[] = [],
): { transform: string; width: number; ready: boolean } {
  const [box, setBox] = useState({ left: 0, width: 0, ready: false });

  useLayoutEffect(() => {
    const measure = () => {
      const el = containerRef.current?.querySelector<HTMLElement>(`[data-pill-index="${activeIndex}"]`);
      // offsetWidth is 0 while the row is display:none (e.g. footer hidden behind a modal): keep the last good box.
      if (el && el.offsetWidth > 0) setBox({ left: el.offsetLeft, width: el.offsetWidth, ready: true });
    };
    measure();
    window.addEventListener('resize', measure);
    return () => window.removeEventListener('resize', measure);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [containerRef, activeIndex, ...deps]);

  return { transform: `translateX(${box.left}px)`, width: box.width, ready: box.ready };
}
